import { PageShell } from '@/components/PageShell';
import { UserDevicesTab } from '@/app/users/UserDevicesTab';
import { UserHomesTab } from '@/app/users/UserHomesTab';
import { UserNotificationsTab } from '@/app/users/UserNotificationsTab';
import { UserOverviewTab } from '@/app/users/UserOverviewTab';
import { ChatConversationPage } from '@/app/users/chats/ChatConversationPage';
import { UserChatsTab } from '@/app/users/chats/UserChatsTab';
import IdWithCopy from '@/components/IdWithCopy';
import { TabNav } from '@/components/TabNav';
import { UserAvatar } from '@/components/UserAvatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { userState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import { UserIdSchema } from '@rock/types';
import { isTestUser } from '@rock/util';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import type { FC } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

export const UserDetailsPage: FC = () => {
    const navigate = useNavigate();
    const { userId: rawUserId } = useParams<{ userId: string }>();
    const userId = rawUserId ? UserIdSchema.parse(rawUserId) : undefined;
    const [user, userLoadable] = useLoadableAtomValue(userState(userId));

    if (!userId) {
        return null;
    }

    if (!user) {
        return (
            <PageShell>
                <div className="text-muted-foreground">
                    {userLoadable.state === 'loading' ? 'Loading user…' : 'User not found.'}
                </div>
            </PageShell>
        );
    }

    const displayName = user.name?.trim() || user.email || user.id;

    return (
        <PageShell>
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" aria-label="Back to users" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <UserAvatar imageUrl={user.imageUrl} name={displayName} className="size-12" />
                <div className="flex min-w-0 flex-col">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="truncate text-2xl font-semibold">{displayName}</h1>
                        {isTestUser(user) && <Badge variant="outline">Test</Badge>}
                        {user.deleted && <Badge variant="destructive">Deleted</Badge>}
                    </div>
                    <IdWithCopy id={user.id} variant="muted" />
                </div>
            </div>

            {user.privacy?.dataDeletionRequestedAt && (
                <div className="flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm">
                    <TriangleAlert className="h-5 w-5 shrink-0 text-destructive" />
                    <div>
                        <span className="font-medium text-destructive">Data deletion requested</span>{' '}
                        <span className="text-muted-foreground">
                            on {formatDateTime(user.privacy.dataDeletionRequestedAt)}
                            {user.privacy.dataDeletionTargetDate &&
                                ` — scheduled for ${user.privacy.dataDeletionTargetDate}`}
                        </span>
                    </div>
                </div>
            )}

            <TabNav
                tabs={[
                    { label: 'Overview', to: `/directory/users/${userId}/overview` },
                    { label: 'Homes', to: `/directory/users/${userId}/homes` },
                    { label: 'Chats', to: `/directory/users/${userId}/chats` },
                    { label: 'Notifications', to: `/directory/users/${userId}/notifications` },
                    { label: 'Devices', to: `/directory/users/${userId}/devices` },
                ]}
            />

            <Routes>
                <Route index element={<Navigate replace to="overview" />} />
                <Route path="overview" element={<UserOverviewTab user={user} />} />
                <Route path="homes" element={<UserHomesTab userId={userId} />} />
                <Route path="chats" element={<UserChatsTab userId={userId} />} />
                <Route path="chats/:conversationId" element={<ChatConversationPage userId={userId} />} />
                <Route path="notifications" element={<UserNotificationsTab userId={userId} />} />
                <Route path="devices" element={<UserDevicesTab userId={userId} />} />
            </Routes>
        </PageShell>
    );
};
